import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpService, DateFormatSettingDto } from 'src/app/@services/http/http.service';

export type AppDateKind = 'date' | 'datetime' | 'long' | 'longDatetime';

export interface AppDateFormatSettings {
  smallDateFormat: string;
  dateFormatWithTime: string;
  longDateFormat: string;
  longDateFormatWithTime: string;
}

const DEFAULT_SETTINGS: AppDateFormatSettings = {
  smallDateFormat: 'DD/MM/YYYY',
  dateFormatWithTime: 'DD/MM/YYYY hh:mm A',
  longDateFormat: 'MMMM DD, YYYY',
  longDateFormatWithTime: 'MMMM DD, YYYY hh:mm A'
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

@Injectable({ providedIn: 'root' })
export class AppDateFormatService {
  private readonly settingsSubject = new BehaviorSubject<AppDateFormatSettings>({ ...DEFAULT_SETTINGS });
  readonly settings$ = this.settingsSubject.asObservable();
  private loaded = false;
  private loading = false;

  constructor(private http: HttpService) {}

  get settings(): AppDateFormatSettings {
    return this.settingsSubject.value;
  }

  /** Ensure settings are loaded (safe to call repeatedly). */
  ensureLoaded(): void {
    if (this.loaded || this.loading) {
      return;
    }
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.http.getDateFormatSetting().subscribe({
      next: (res) => {
        this.applyDto(res?.data);
        this.loaded = true;
        this.loading = false;
      },
      error: () => {
        this.settingsSubject.next({ ...DEFAULT_SETTINGS });
        this.loaded = true;
        this.loading = false;
      }
    });
  }

  applyLocal(settings: Partial<AppDateFormatSettings>): void {
    this.settingsSubject.next({
      ...this.settingsSubject.value,
      ...settings
    });
    this.loaded = true;
  }

  format(value: unknown, kind: AppDateKind = 'datetime', empty = '—'): string {
    if (value == null || value === '' || value === 'null') {
      return empty;
    }

    const pattern = this.patternFor(kind);
    if (kind === 'date' || kind === 'long') {
      const parts = this.extractDateOnlyParts(value);
      if (!parts) {
        return empty;
      }
      return this.applyPattern(pattern, parts, false);
    }

    const parts = this.extractDateTimeParts(value);
    if (!parts) {
      return empty;
    }
    return this.applyPattern(pattern, parts, true);
  }

  /** Current small date pattern from settings (moment-style tokens). */
  get smallDateFormat(): string {
    return this.settings.smallDateFormat || DEFAULT_SETTINGS.smallDateFormat;
  }

  /**
   * Pattern for `nz-date-picker` `[nzFormat]` (date-fns tokens).
   * Converts Date Format Setting patterns like `DD/MM/YYYY` → `dd/MM/yyyy`.
   */
  pickerDateFormat(): string {
    return this.toDateFnsFormat(this.smallDateFormat);
  }

  /** Convert moment-style tokens used in Date Format Setting to date-fns for Ant Design. */
  toDateFnsFormat(pattern: string): string {
    if (!pattern) {
      return 'dd/MM/yyyy';
    }
    const map: Record<string, string> = {
      YYYY: 'yyyy',
      YY: 'yy',
      MMMM: 'MMMM',
      MMM: 'MMM',
      MM: 'MM',
      M: 'M',
      DD: 'dd',
      D: 'd',
      dddd: 'EEEE',
      HH: 'HH',
      H: 'H',
      hh: 'hh',
      h: 'h',
      mm: 'mm',
      ss: 'ss',
      A: 'a'
    };
    const tokenOrder = ['dddd', 'MMMM', 'YYYY', 'MMM', 'YY', 'MM', 'DD', 'HH', 'hh', 'mm', 'ss', 'A', 'M', 'D', 'H', 'h'];
    let out = '';
    let i = 0;
    while (i < pattern.length) {
      let matched = false;
      for (const token of tokenOrder) {
        if (pattern.startsWith(token, i)) {
          out += map[token] || token;
          i += token.length;
          matched = true;
          break;
        }
      }
      if (!matched) {
        out += pattern[i];
        i += 1;
      }
    }
    return out;
  }

  private patternFor(kind: AppDateKind): string {
    const s = this.settings;
    switch (kind) {
      case 'date':
        return s.smallDateFormat || DEFAULT_SETTINGS.smallDateFormat;
      case 'datetime':
        return s.dateFormatWithTime || DEFAULT_SETTINGS.dateFormatWithTime;
      case 'long':
        return s.longDateFormat || DEFAULT_SETTINGS.longDateFormat;
      case 'longDatetime':
        return s.longDateFormatWithTime || DEFAULT_SETTINGS.longDateFormatWithTime;
      default:
        return s.dateFormatWithTime || DEFAULT_SETTINGS.dateFormatWithTime;
    }
  }

  private applyDto(dto?: DateFormatSettingDto | null): void {
    if (!dto) {
      this.settingsSubject.next({ ...DEFAULT_SETTINGS });
      return;
    }
    this.settingsSubject.next({
      smallDateFormat: dto.small_date_format || dto.smallDateFormat || DEFAULT_SETTINGS.smallDateFormat,
      dateFormatWithTime: dto.date_format_with_time || dto.dateFormatWithTime || DEFAULT_SETTINGS.dateFormatWithTime,
      longDateFormat: dto.long_date_format || dto.longDateFormat || DEFAULT_SETTINGS.longDateFormat,
      longDateFormatWithTime:
        dto.long_date_format_with_time || dto.longDateFormatWithTime || DEFAULT_SETTINGS.longDateFormatWithTime
    });
  }

  /**
   * Calendar date only — prefer YYYY-MM-DD embedded in the string so UTC midnight
   * does not shift the day in western timezones.
   */
  private extractDateOnlyParts(value: unknown): DateParts | null {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return {
        year: value.getUTCFullYear(),
        month: value.getUTCMonth() + 1,
        day: value.getUTCDate(),
        hours24: 0,
        minutes: 0,
        seconds: 0,
        weekday: value.getUTCDay()
      };
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      if (isNaN(d.getTime())) {
        return null;
      }
      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
        hours24: 0,
        minutes: 0,
        seconds: 0,
        weekday: d.getUTCDay()
      };
    }

    const s = String(value).trim();
    if (!s) {
      return null;
    }

    const dmy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
    if (dmy) {
      const day = Number(dmy[1]);
      const month = Number(dmy[2]);
      const year = Number(dmy[3]);
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
        return { year, month, day, hours24: 0, minutes: 0, seconds: 0, weekday: this.weekdayUtc(year, month, day) };
      }
    }

    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      const year = Number(ymd[1]);
      const month = Number(ymd[2]);
      const day = Number(ymd[3]);
      return { year, month, day, hours24: 0, minutes: 0, seconds: 0, weekday: this.weekdayUtc(year, month, day) };
    }

    return null;
  }

  private extractDateTimeParts(value: unknown): DateParts | null {
    let d: Date | null = null;

    if (value instanceof Date) {
      d = value;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      d = new Date(ms);
    } else {
      const s = String(value).trim();
      if (!s) {
        return null;
      }
      // Pure date string → treat as UTC midnight calendar day displayed without inventing local time shift for "date" kind;
      // for datetime, still parse as local noon-safe by using date-only parts + 00:00 local.
      const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymd) {
        return {
          year: Number(ymd[1]),
          month: Number(ymd[2]),
          day: Number(ymd[3]),
          hours24: 0,
          minutes: 0,
          seconds: 0,
          weekday: this.weekdayUtc(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]))
        };
      }
      d = new Date(s);
    }

    if (!d || isNaN(d.getTime())) {
      return null;
    }

    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hours24: d.getHours(),
      minutes: d.getMinutes(),
      seconds: d.getSeconds(),
      weekday: d.getDay()
    };
  }

  private weekdayUtc(year: number, month: number, day: number): number {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  }

  private applyPattern(pattern: string, parts: DateParts, includeTime: boolean): string {
    const hours24 = parts.hours24;
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';

    const map: Record<string, string> = {
      dddd: WEEKDAYS[parts.weekday] || '',
      MMMM: MONTHS_LONG[parts.month - 1] || '',
      YYYY: String(parts.year),
      MMM: MONTHS_SHORT[parts.month - 1] || '',
      YY: String(parts.year).slice(-2),
      MM: this.pad2(parts.month),
      DD: this.pad2(parts.day),
      HH: this.pad2(hours24),
      hh: this.pad2(hours12),
      mm: this.pad2(parts.minutes),
      ss: this.pad2(parts.seconds),
      A: ampm,
      M: String(parts.month),
      D: String(parts.day),
      H: String(hours24),
      h: String(hours12)
    };

    const tokenOrder = includeTime
      ? ['dddd', 'MMMM', 'YYYY', 'MMM', 'YY', 'MM', 'DD', 'HH', 'hh', 'mm', 'ss', 'A', 'M', 'D', 'H', 'h']
      : ['dddd', 'MMMM', 'YYYY', 'MMM', 'YY', 'MM', 'DD', 'M', 'D'];

    let out = '';
    let i = 0;
    while (i < pattern.length) {
      let matched = false;
      for (const token of tokenOrder) {
        if (pattern.startsWith(token, i)) {
          out += map[token];
          i += token.length;
          matched = true;
          break;
        }
      }
      if (!matched) {
        out += pattern[i];
        i += 1;
      }
    }
    return out;
  }

  private pad2(n: number): string {
    return String(n).padStart(2, '0');
  }
}

interface DateParts {
  year: number;
  month: number;
  day: number;
  hours24: number;
  minutes: number;
  seconds: number;
  weekday: number;
}
